import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { taggedLogger } from '@/util/taggedLogger';
import { useHipSpineFeatureGate } from '@/stores/featureToggles/useHipSpineFeatureGate';
import { useCaseSyncStore } from '@/stores/useCaseSyncStore';
import { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
import { useHipSpineSyncStore } from '@/views/3d-planner/hipSpine/hipSpineSyncStore';

const log = taggedLogger('hip-spine-loading');

/**
 * ViewModel for the planner's hip-spine loading orchestration. It applies the composed
 * hip-spine gate, loads the study for the active case, and exposes the study-completion signal
 * that drives the save-sync engine. Extracted from PlannerView so the view touches no hip-spine
 * Model store directly (MVVM boundary) — the view keeps only its route-driven watchers and
 * delegates here.
 */
export const useHipSpineLoadingViewModel = defineStore('hip-spine-loading', () => {
    const { enabled: featureEnabled } = useHipSpineFeatureGate();
    const caseStore = useCaseSyncStore();
    const hipSpineStore = useHipSpineStore();
    const hipSpineSync = useHipSpineSyncStore();

    const hipSpineStudyId = computed(() => caseStore.hipSpineStudyId);
    const isStudyCompleted = computed(() => hipSpineStore.isStudyCompleted);

    /** The case the view last asked for, remembered even while the gate is off. */
    const lastRequestedCaseId = ref<number | null>(null);

    async function loadStudy(caseId: number): Promise<void> {
        lastRequestedCaseId.value = caseId;
        if (!featureEnabled.value) return;
        try {
            await hipSpineStore.loadHipSpineStudy(caseId, hipSpineStudyId.value);
            if (hipSpineStore.isStudyProcessing) {
                hipSpineStore.pollStudyUntilTerminal().catch(() => {
                    log.error('Hip spine study polling failed');
                });
            }
        } catch {
            // Prevent unhandled promise rejections from loadHipSpineStudy
            log.error('Failed to load hip spine study');
        }
    }

    // The Amplitude half of the gate resolves asynchronously, so it can turn on after the view's
    // own route watcher has already run and been turned away. Re-attempt on the off → on
    // transition only. A plain (non-immediate) watch does not fire on registration, so a gate
    // that is already on produces exactly one load — the view's — with no double-fire.
    watch(featureEnabled, (isOn, wasOn) => {
        if (!isOn || wasOn === true) return;
        if (lastRequestedCaseId.value === null) return;
        void loadStudy(lastRequestedCaseId.value);
    });

    function setSyncEnabled(enabled: boolean): void {
        if (enabled) hipSpineSync.startSync();
        else hipSpineSync.stopSync();
    }

    return { hipSpineStudyId, isStudyCompleted, loadStudy, setSyncEnabled };
});
